<script>
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { smoothScrollIntoView } from '../../utils/side-effects';
  
  let {
    socialIcons,
    siteLinks,
    termsLinks,
    badges = undefined,
    socialMediaText,
    spanishLabel,
    englishLabel,
    topPageLinkText,
    copyrightText,
    globalDisclaimer = undefined,
    hideGlobalDisclaimer = false,
    pageDisclaimer = undefined,
    lang,
    spanishLink = undefined,
    englishLink = undefined,
    dataTestId = 'the-footer',
  } = $props();

  // to respond to changes in the English and Spanish link props
  let englishLinkObj = $derived(englishLink);
  let spanishLinkObj = $derived(spanishLink);

  const handleBackToTopButtonClicked = (e) => {
    // see whether user activated this with click of mouse or enter key
    // if the latter, go to skip link. We want focus to be on the top of the page instead of on the bottom.
    // else, just do the scroll the to top
    if (e.detail > 0) smoothScrollIntoView();
    else document.getElementsByClassName('skip-link')[0].focus();
  };
</script>

<footer class="flex print:hidden flex-col" data-testid={dataTestId}>
  <!-- Link Section -->
  <div class="flex flex-wrap border-t border-gray-light-silver">
    <div class="w-full max-w-6xl mx-auto">
      <nav
        aria-labelledby="footer-nav-links"
        class="w-full px-4 pt-5 pb-2 columns-2 sm:columns-3 xl:pt-6 xl:pb-3 xl:columns-4"
        data-testid="the-footer-link-group-link"
      >
        {#each siteLinks as link, index (index)}
          <div class="mb-3">
            <BaseLink
              className="text-sm leading-tight text-gray-charcoal break-words hover-hover:hover:underline
              hover-hover:hover:text-blue-cerulean active:text-blue-cerulean active:underline"
              {...link}
            />
          </div>
        {/each}
      </nav>
    </div>
  </div>
  <!-- Terms Section -->
  <div class="xl:bg-gray-pale border-t border-gray-light-silver">
    <div class="bg-gray-pale w-full xl:flex xl:items-baseline xl:max-w-6xl xl:mx-auto">
      <div class="max-w-6xl mx-auto xl:mx-0">
        <nav
          aria-labelledby="footer-nav-terms"
          class="flex flex-col px-4 pb-4 lg:pt-4 lg:block lg:columns-3 xl:pt-0 xl:flex xl:flex-row xl:flex-wrap"
          data-testid="the-footer-link-group-terms"
        >
          {#each termsLinks as link, index (index)}
            <div class="mt-3" class:lg:mt-0={index === 0} class:xl:mt-3={index === 0}>
              <BaseLink
                className="text-sm leading-tight text-gray-charcoal break-words hover-hover:hover:underline
                hover-hover:hover:text-blue-cerulean active:underline active:text-blue-cerulean xl:mr-4"
                {...link}
              />
            </div>
          {/each}
          <!-- Display OneTrust Cookies Settings button in Terms menu group -->
          <button
            aria-label="Click button for Cookie Preferences"
            class="mt-3 ot-sdk-show-settings text-gray-charcoal text-sm text-left hover-hover:hover:text-blue-cerulean hover-hover:hover:underline active:text-blue-cerulean active:underline"
          >
          </button>
        </nav>
      </div>      
    </div>
  </div>
  <!-- Copywrite, Language Selection, Back to Top -->
  <div class="bg-blue-navy">
    <div class="flex flex-wrap items-center max-w-6xl mx-auto px-4">
      <div class="pt-8 pb-10 w-full relative">
        <!-- Language Selection -->
        <svg role="presentation" class="w-6 h-6 inline mr-4 fill-current text-white">
          <use xlink:href="{SPRITESHEET_URL}language-globe" />
        </svg>
        {#if englishLinkObj && englishLinkObj.url}
          <BaseLink
            className="mr-4 inline-block text-lg text-white opacity-50 hover-hover:hover:opacity-75 active:opacity-100"
            {...englishLinkObj}
            dataTestId="the-footer-language-en"
          >
            {englishLabel}
          </BaseLink>
        {:else if lang === 'en'}
          <span
            class="mr-4 inline-block text-lg text-white border-white border-b-2 font-semibold"
            data-testid="the-footer-language-en"
          >
            {englishLabel}
          </span>
        {/if}
        {#if spanishLinkObj && spanishLinkObj.url}
          <BaseLink
            className="mr-4 inline-block text-lg text-white opacity-50 hover-hover:hover:opacity-75 active:opacity-100"
            {...spanishLinkObj}
            dataTestId="the-footer-language-es"
          >
            {spanishLabel}
          </BaseLink>
        {:else if lang === 'es'}
          <span
            class="mr-4 inline-block text-lg text-white border-white border-b-2 font-semibold"
            data-testid="the-footer-language-es"
          >
            {spanishLabel}
          </span>
        {/if}
        <!-- Back to Top -->
        <button
          class="absolute right-0 group mt-1 text-blue-bright-cyan hover-hover:hover:text-blue-robin-egg active:text-blue-robin-egg"
          onclick={handleBackToTopButtonClicked}
        >
          <span
            class="mr-2 text-xs leading-normal text-right font-semibold hover-hover:group-hover:underline group-active:underline group-active:text-blue-robin-egg inline"
          >
            {topPageLinkText}
          </span>
          <svg role="presentation" class="w-4 h-4 inline fill-current -rotate-90">
            <use xlink:href="{SPRITESHEET_URL}right-arrow" />
          </svg>
        </button>
      </div>
    </div>
    <!-- Copyright -->
    <div class="w-full max-w-6xl mx-auto lg:flex">
      <div class="pb-8 px-4 text-sm text-white">
        {@html copyrightText.content}
      </div>

      <!-- Social Icons -->
      <div class="bg-white border-gray-light-silver border-b w-full max-w-6xl mx-auto flex justify-end items-center px-4 py-3 lg:items-end lg:pt-0 lg:pb-8 lg:border-b-0 lg:w-auto lg:bg-transparent lg:mr-0">
        <span class="hidden sm:block text-base font-semibold mr-auto leading-none lg:text-white lg:mr-4">{socialMediaText}</span>
        {#each socialIcons as { link, icon }, index (index)}
          {#if link && link.url && link.text}
            <BaseLink
              className="mr-4 last:mr-0 text-gray-warm lg:text-white hover-hover:hover:text-blue-cerulean active:text-blue-cerulean group xl:mr-3"
              {...link}
              ariaLabel={link.text}
            >
              <svg role="presentation" class="max-w-7 h-5 inline fill-current">
                <use xlink:href="{SPRITESHEET_URL}{icon}" />
              </svg>
              <span class="hidden hover-hover:group-hover:underline group-active:underline">{link.text}</span>
            </BaseLink>
          {/if}
        {/each}
      </div>
    </div>
  </div>
  <!-- Badges, Global Disclaimer, Page Disclaimer-->
  <div class="px-4 pt-8 bg-white">
    <div class="flex flex-wrap items-center max-w-6xl mx-auto">
      <!-- Badges -->
      {#if badges}
        <div class="flex flex-row flex-wrap justify-center items-center w-full mb-8">
          {#each badges as { link, image }, index (index)}
            <BaseLink className="m-4 h-15 max-h-15" {...link} ariaLabel={link.text}>
              <img
                class="object-scale-down h-15 w-auto"
                loading="lazy"
                height="60"
                width={link.text === 'Dot Pharamcy' ? 210 : 70}
                src={image.url}
                alt={image.alt}
              />
            </BaseLink>
          {/each}
        </div>
      {/if}

      <!-- Global Disclaimer - Move Outside of this Section -->
      {#if globalDisclaimer && globalDisclaimer.content && !hideGlobalDisclaimer}
        <div
          class="pb-8 text-sm leading-tight text-gray-warm mx-auto max-w-4xl"
          data-testid="the-footer-global-disclaimer"
        >
          {@html globalDisclaimer.content}
        </div>
      {/if}

      <!-- Page Disclaimer - Move Outside of this Section -->
      {#if pageDisclaimer && pageDisclaimer.content}
        <div
          class="pb-8 text-sm leading-tight text-gray-warm mx-auto max-w-4xl"
          data-testid="the-footer-page-disclaimer"
        >
          {@html pageDisclaimer.content}
        </div>
      {/if}
    </div>
  </div>
</footer>
